import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { CellContextMenu, canBulkCopy } from '../../components/gridEdit';
import { useNotify } from '../../components/notifications';
import { fetchStockStdOptions } from './api';
import { LEVEL_COLS, LEVEL_EDIT } from './componentColumns';
import { NA_OPTION, type ReagentLookupOption, type ReagentPrepComponent, type ReagentPrepDetail } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// The Calibration levels grid — XAF ReagentPreparation_ReagentPrepLogs_ListView_
// Calibration, AllowEdit="True" InlineEditMode="Batch" (Model.xafml :38674).
//
// Edits are BUFFERED by the detail page and stored with the header's Save, as
// in XAF (the nested list shares the detail view's ObjectSpace). This component
// only edits the buffer; it never saves.
//
//   · typed columns   — click a cell (the shared batch-edit cell editor)
//   · unit columns    — a ReagentUnits combo
//   · StockStdName    — a plain dropdown like the unit columns (owner request
//                       2026-10-26, replacing XAF's popup picker). Its list is the
//                       API's (the header stock, then the earlier levels' working
//                       standards — ctrl :202-235), loaded per level up front.
//   · right-click     — the shared Copy to All Cells / Copy to Selected Rows
//                       menu (XAF "CopyToAllCell" copies to the ticked rows)
//
// Formula and FinalConc are NOT computed here: the API recomputes them from
// WSconc / VolTaken / FinalVol on save (CalibrationLevelRules), so the numbers
// do not depend on this component having run (CLAUDE.md §1).
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  prepId: string | null;
  prep: ReagentPrepDetail;
  /** The levels with the buffered edits already applied. */
  rows: ReagentPrepComponent[];
  units: ReagentLookupOption[];
  loading: boolean;
  /** The detail is in View mode: no cell editors and no Copy-to menu. */
  readOnly?: boolean;
  /** Apply a patch to some levels' buffer. */
  onEdit: (oids: string[], patch: Partial<ReagentPrepComponent>) => void;
}

const EMPTY_ID = '00000000-0000-0000-0000-000000000000';

export function CalibrationLevelsGrid({ prepId, prep, rows, units, loading, readOnly = false, onEdit }: Props) {
  const notify = useNotify();
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [menu, setMenu] = useState<{
    x: number; y: number; field: keyof ReagentPrepComponent; label: string; row: ReagentPrepComponent;
  } | null>(null);
  // StockStdName options per level. The list depends on the header stock and on
  // the working standards typed into the levels, so it reloads when those change.
  const [stdOptions, setStdOptions] = useState<Record<string, string[]>>({});
  const stdKey = JSON.stringify([prep.selectStockSolution, prep.vendorStockId, prep.labStockId,
    rows.map((r) => [r.oid, r.componentID, r.workingStdName])]);

  const unitNames = useMemo(() => units.map((u) => u.name ?? '').filter(Boolean), [units]);

  /** The patch for setting one column to a value — a unit column carries its Id. */
  function patchFor(field: keyof ReagentPrepComponent, value: string | null, source?: ReagentPrepComponent) {
    const spec = LEVEL_EDIT[field];
    // N/A is the client-only placeholder: picking it CLEARS (never stored as text).
    const v = value === '' || value === NA_OPTION.label ? null : value;
    if (spec?.kind === 'unit') {
      const id = source ? (source[spec.idKey] as string | null | undefined) ?? null
                        : units.find((u) => u.name === v)?.id ?? null;
      return { [field]: v, [spec.idKey]: id } as Partial<ReagentPrepComponent>;
    }
    return { [field]: v } as Partial<ReagentPrepComponent>;
  }

  useEffect(() => {
    if (rows.length === 0) { setStdOptions({}); return; }
    let live = true;
    const levels = rows.map((r) => ({ oid: r.oid, componentID: Number(r.componentID ?? 0), workingStdName: r.workingStdName }));
    // The list is built from the request (header stock + these levels), so a
    // NEW prep, which has no id yet, gets it too.
    Promise.all(rows.map((r) => fetchStockStdOptions(prepId ?? EMPTY_ID, {
      levelOid: r.oid,
      selectStockSolution: prep.selectStockSolution,
      vendorStockId: prep.vendorStockId,
      labStockId: prep.labStockId,
      levels,
    }).then((o) => [r.oid, o] as const)))
      .then((pairs) => { if (live) setStdOptions(Object.fromEntries(pairs)); })
      .catch((e) => notify.error(e instanceof Error ? e.message : 'Failed to load the StockStdName list.'));
    return () => { live = false; };
    // stdKey carries every input the lists depend on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stdKey, prepId, notify]);

  function onContext(e: ReactMouseEvent<HTMLTableCellElement>, row: ReagentPrepComponent, field: keyof ReagentPrepComponent, label: string) {
    e.preventDefault();
    if (!canBulkCopy(row[field])) { notify.warning('Cell is empty.'); return; }
    setMenu({ x: e.clientX, y: e.clientY, field, label, row });
  }

  const columns: AppColumn<ReagentPrepComponent>[] = LEVEL_COLS.map((col) => {
    const field = col.field as keyof ReagentPrepComponent;
    const spec = LEVEL_EDIT[field];
    if (!spec || readOnly) return col;
    const ctx = (e: ReactMouseEvent<HTMLTableCellElement>, row: ReagentPrepComponent) =>
      onContext(e, row, field, String(col.header));
    return {
      ...col,
      onCellContextMenu: ctx,
      edit: spec.kind === 'unit'
        ? { type: 'combo' as const, options: [NA_OPTION.label, ...unitNames], clearable: true }
        : spec.kind === 'pick'
          ? { type: 'combo' as const, options: (row: ReagentPrepComponent) => stdOptions[row.oid] ?? [], clearable: true }
          : { type: 'text' as const },
    };
  });

  return (
    <>
      <AppDataGrid<ReagentPrepComponent>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.oid}
        loading={loading}
        emptyText="No data to display"
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onCellCommit={readOnly ? undefined : (row, col, value) => {
          const field = col.field as keyof ReagentPrepComponent;
          onEdit([row.oid], patchFor(field, value));
        }}
      />

      {menu && (
        <CellContextMenu
          value={menu.row[menu.field]}
          columnLabel={menu.label}
          selectedCount={selected.size}
          position={{ x: menu.x, y: menu.y }}
          onClose={() => setMenu(null)}
          onCopyAll={() => {
            onEdit(rows.map((r) => r.oid), patchFor(menu.field, menu.row[menu.field] as string | null, menu.row));
            setMenu(null);
          }}
          onCopySelected={() => {
            if (selected.size === 0) { notify.warning('Select at least one row.'); setMenu(null); return; }
            onEdit([...selected].map(String), patchFor(menu.field, menu.row[menu.field] as string | null, menu.row));
            setMenu(null);
          }}
        />
      )}
    </>
  );
}
